import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Pause, Play } from "lucide-react";

function fmtClock(s) {
  if (!s && s !== 0) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
}

// Une seule lecture inline active à la fois (évite la cacophonie).
let CURRENT_INLINE_AUDIO = null;

/**
 * Lecteur audio compact en ligne : play/pause + barre de progression cliquable
 * + minuteur. L'audio (blob authentifié) n'est chargé qu'au 1er clic sur play.
 * Récupéré via GET /calls/{callId}/recording.
 */
export function InlineAudioPlayer({ callId, totalSec, onPlay, className = "" }) {
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(totalSec || 0);
  const [error, setError] = useState(false);
  const audioRef = useState(() => (typeof Audio !== "undefined" ? new Audio() : null))[0];

  useEffect(() => {
    const a = audioRef;
    if (!a) return;
    const onTime = () => setCur(a.currentTime || 0);
    const onMeta = () => { if (isFinite(a.duration)) setDur(a.duration); };
    const onEnd = () => { setPlaying(false); setCur(0); };
    const onPause = () => setPlaying(false);
    const onPlayEv = () => setPlaying(true);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    a.addEventListener("pause", onPause);
    a.addEventListener("play", onPlayEv);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("play", onPlayEv);
      try { a.pause(); } catch { /* noop */ }
      if (a.src) { URL.revokeObjectURL(a.src); a.removeAttribute("src"); }
      if (CURRENT_INLINE_AUDIO === a) CURRENT_INLINE_AUDIO = null;
    };
    // eslint-disable-next-line
  }, []);

  const toggle = async () => {
    const a = audioRef;
    if (!a) return;
    if (playing) { a.pause(); return; }
    onPlay?.();
    if (CURRENT_INLINE_AUDIO && CURRENT_INLINE_AUDIO !== a) { try { CURRENT_INLINE_AUDIO.pause(); } catch { /* noop */ } }
    CURRENT_INLINE_AUDIO = a;
    if (!a.src) {
      setLoading(true); setError(false);
      try {
        const res = await api.get(`/calls/${callId}/recording`, { responseType: "blob" });
        a.src = URL.createObjectURL(res.data);
      } catch {
        setError(true); setLoading(false);
        toast.error("Enregistrement indisponible.");
        return;
      }
      setLoading(false);
    }
    try { await a.play(); } catch { /* autoplay guard */ }
  };

  const seek = (e) => {
    const a = audioRef;
    if (!a || !dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    a.currentTime = ratio * dur;
    setCur(a.currentTime);
  };

  const pct = dur ? Math.min(100, (cur / dur) * 100) : 0;

  return (
    <div data-inline-player className={`flex items-center gap-2 ${className}`} data-testid={`inline-player-${callId}`}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); toggle(); }}
        disabled={loading}
        aria-label={playing ? "Pause" : "Lecture"}
        data-testid={`inline-play-${callId}`}
        className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[var(--shariti-orange)] text-white transition-transform hover:scale-105 active:scale-95 disabled:opacity-60"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 translate-x-[1px]" />}
      </button>
      <div
        onClick={(e) => { e.stopPropagation(); seek(e); }}
        className="group relative h-2 flex-1 cursor-pointer rounded-full bg-[var(--surface-2)]"
        data-testid={`inline-seek-${callId}`}
      >
        <div className="absolute inset-y-0 left-0 rounded-full bg-[var(--shariti-orange)]" style={{ width: `${pct}%` }} />
      </div>
      <span className="shrink-0 font-mono text-[11px] tabular-nums text-muted-ink" data-testid={`inline-time-${callId}`}>
        {error ? "indispo." : `${fmtClock(cur)} / ${fmtClock(dur)}`}
      </span>
    </div>
  );
}

export default InlineAudioPlayer;
